import { t } from 'i18next';
import { OnboardingLogo, isCheckedIcon, notCheckedIcon } from 'icons';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { profileCompletedApi } from 'data/api/userApi';

export const Sidebar = ({
  renderStep,
  currentStep,
}: {
  renderStep: any;
  currentStep: string;
}) => {
  const navigate = useNavigate();
  const [profileCompleted, setProfileCompleted] = useState(true); // Default to true to avoid flash
  const tabsScrollerRef = useRef<HTMLOListElement>(null);
  const tabRefs = useRef<Record<string, HTMLLIElement | null>>({});

  const steps = [
    { id: 'step1', label: t('profileSettings.title') },
    { id: 'step2', label: t('personalData.title') },
    { id: 'step3', label: t('personalIdentification.title') },
    { id: 'step4', label: t('bankData.title') },
  ];

  useEffect(() => {
    const fetchProfileStatus = async () => {
      try {
        const profileCompletedResp = await profileCompletedApi();
        setProfileCompleted(profileCompletedResp.status);
      } catch (error) {
        console.error('Error fetching profile completed status:', error);
      }
    };

    fetchProfileStatus();
  }, []);

  useEffect(() => {
    const scroller = tabsScrollerRef.current;
    const tab = tabRefs.current[currentStep];
    if (!scroller || !tab) return;

    const left = tab.offsetLeft - (scroller.clientWidth - tab.offsetWidth) / 2;
    scroller.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
  }, [currentStep]);

  const warning = !profileCompleted && (
    <div className="p-3 bg-yellow-100 border border-yellow-300 rounded-md">
      <p className="text-sm text-yellow-800 font-medium leading-relaxed">
        {t('sidebar.profileWarning')}
      </p>
    </div>
  );

  return (
    <>
      {/* Phones and small tablets: the four steps stay visible as a scrollable row. */}
      <div className="md:hidden bg-[#2E435F] px-[12px] py-[12px] flex flex-col gap-[10px]">
        <nav aria-label={t('profileSettings.title') as string}>
          <ol
            ref={tabsScrollerRef}
            className="flex gap-[8px] overflow-x-auto hide-scrollbar scroll-smooth"
          >
            {steps.map((step, index) => {
              const active = currentStep === step.id;
              return (
                <li
                  key={step.id}
                  ref={(el) => {
                    tabRefs.current[step.id] = el;
                  }}
                  className="shrink-0"
                >
                  <button
                    onClick={() => renderStep(step.id)}
                    aria-current={active ? 'step' : undefined}
                    className={`min-h-[42px] px-[14px] rounded-full text-[14px] font-medium flex items-center gap-[8px] whitespace-nowrap ${
                      active
                        ? 'bg-white text-[#2E435F]'
                        : 'bg-white/10 text-white active:bg-white/20'
                    }`}
                  >
                    <span className="tnum opacity-70">{index + 1}</span>
                    {step.label}
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>
        {warning}
      </div>

      {/* Desktop: persistent step rail. */}
      <div className="hidden md:flex md:flex-col h-below-header bg-[#2E435F] w-[272px] border-r-0 border-[#E4E7EC]">
        <div className="flex items-center justify-center w-full pt-[19px] px-6 py-[8px]">
          <button
            className="flex align-middle object-contain cursor-pointer self-center"
            onClick={() => navigate('/')}
          >
            <OnboardingLogo />
          </button>
        </div>

        <div className="w-full px-[8px] py-4 space-y-1">
          {steps.map((step) => (
            <button
              key={step.id}
              className={`${
                currentStep === step.id
                  ? 'bg-[#ffffff] text-black'
                  : 'text-white hover:text-black bg-transparent hover:bg-[#ffffff]'
              } flex items-center px-4 py-2 rounded-md w-full text-left font-medium`}
              onClick={() => renderStep(step.id)}
            >
              <span className="mr-3">
                {currentStep === step.id ? isCheckedIcon() : notCheckedIcon()}
              </span>
              {step.label}
            </button>
          ))}

          <div className="pt-4 px-2">{warning}</div>
        </div>
      </div>
    </>
  );
};
